import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import HarvestView from './components/harvest/HarvestView';
import CaptureView from './components/capture/CaptureView';
import LibraryView from './components/library/LibraryView';
import AdminView from './components/admin/AdminView';
import AuthCallback from './components/auth/AuthCallback';
import Unauthorized from './components/auth/Unauthorized';
import SignedOut from './components/auth/SignedOut';
import { auth } from './services/api';
import { getLoginUrl } from './config/auth';
import Button from './components/common/Button';
import Card from './components/common/Card';
import Table from './components/common/Table';
import Modal from './components/common/Modal';
import Input from './components/common/Input';
import Select from './components/common/Select';

function App() {
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSize, setModalSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [searchValue, setSearchValue] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [utilityType, setUtilityType] = useState('');
  const [customerSelect, setCustomerSelect] = useState('');
  const [statusSelect, setStatusSelect] = useState('');
  const [currentView, setCurrentView] = useState('dashboard');
  const [adminResetTrigger, setAdminResetTrigger] = useState(0);
  const [selectedQueueItem, setSelectedQueueItem] = useState<any>(null);
  const [correctionType, setCorrectionType] = useState<'review' | 'error'>('review');
  const [harvestFilter, setHarvestFilter] = useState<'all' | 'due' | 'missing' | 'overdue'>('all');
  const [harvestTab, setHarvestTab] = useState<'picklist' | 'upload'>('picklist');
  const [harvestUploadFilter, setHarvestUploadFilter] = useState<'pending' | 'failed'>('pending');
  const [captureTab, setCaptureTab] = useState<'processing' | 'exceptions'>('processing');
  const [captureQueue, setCaptureQueue] = useState<'review' | 'error' | 'success' | 'unknown' | 'duplicate' | undefined>(undefined);

  useEffect(() => {
    const authPaths = ['/auth/callback', '/unauthorized', '/signed-out'];
    if (authPaths.includes(location.pathname)) {
      setAuthChecked(true);
      return;
    }

    if (!auth.isAuthenticated()) {
      getLoginUrl().then(url => {
        window.location.href = url;
      }).catch(err => {
        console.error('Failed to get login URL:', err);
        setAuthChecked(true);
      });
      return;
    }

    setAuthChecked(true);
  }, [location.pathname]);

  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <img
          src="/joule-logo.png"
          alt="Joule"
          style={{ height: '2.5rem', marginBottom: '2rem' }}
        />
        <p style={{ color: '#666', fontSize: '0.875rem' }}>Loading...</p>
      </div>
    );
  }

  const handleButtonClick = () => {
    console.log('Button clicked!');
  };

  const handleCardClick = () => {
    console.log('Card clicked!');
  };

  const handleRowClick = (row: Record<string, any>, index: number) => {
    console.log('Row clicked:', row, 'Index:', index);
  };

  const openModal = (size: 'small' | 'medium' | 'large' = 'medium') => {
    setModalSize(size);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const tableColumns = [
    { key: 'id', title: 'ID', width: '80px', align: 'center' as const },
    { key: 'customer', title: 'Customer Name', width: '200px' },
    { key: 'utility', title: 'Utility Type', width: '120px' },
    { key: 'amount', title: 'Amount Due', width: '120px', align: 'right' as const },
    { key: 'status', title: 'Status', width: '100px', align: 'center' as const },
    { key: 'date', title: 'Due Date', width: '120px' }
  ];

  const tableData = [
    {
      id: '1001',
      customer: 'Allegheny Center Alliance Church',
      utility: 'Electric',
      amount: '$125.67',
      status: 'Pending',
      date: '2024-08-15'
    },
    {
      id: '1002',
      customer: 'Allegheny Center Alliance Church',
      utility: 'Water',
      amount: '$89.23',
      status: 'Overdue',
      date: '2024-08-10'
    },
    {
      id: '1003',
      customer: 'Sample Property LLC',
      utility: 'Gas',
      amount: '$156.89',
      status: 'Received',
      date: '2024-08-20'
    }
  ];

  const handleNavigateWithFilter = (view: string, filter?: any) => {
    setCurrentView(view);
    if (view === 'harvest' && filter) {
      setHarvestFilter(filter);
      setHarvestTab('picklist');
      setCaptureQueue(undefined); // Clear capture queue when navigating to harvest
    } else if (view === 'capture' && filter) {
      setCaptureQueue(filter);
      // Set the correct tab based on the queue
      if (filter === 'duplicate' || filter === 'unknown') {
        setCaptureTab('exceptions');
      } else {
        setCaptureTab('processing');
      }
      setHarvestFilter('all'); // Clear harvest filter when navigating to capture
    }
  };

  const handleSidebarNavigate = (view: string) => {
    setCurrentView(view);

    // Reset all navigation states to defaults when coming from sidebar
    if (view === 'harvest') {
      setHarvestFilter('due');
      setHarvestTab('picklist');
      setHarvestUploadFilter('pending');
      setCaptureQueue(undefined);
    } else if (view === 'capture') {
      setCaptureTab('processing');
      setCaptureQueue('review');
      setHarvestFilter('all');
    } else if (view === 'admin') {
      // Trigger admin reset to customer list
      setAdminResetTrigger(prev => prev + 1);
    } else {
      // Reset states for other views
      setHarvestFilter('all');
      setCaptureQueue(undefined);
      setHarvestTab('picklist');
      setHarvestUploadFilter('pending');
      setCaptureTab('processing');
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard onNavigate={(view, filter) => {
          if (view === 'capture-review') {
            handleNavigateWithFilter('capture', 'review');
          } else if (view === 'capture-error') {
            handleNavigateWithFilter('capture', 'error');
          } else if (view === 'capture-success') {
            handleNavigateWithFilter('capture', 'success');
          } else if (view === 'capture-duplicate') {
            setCurrentView('capture');
            setCaptureTab('exceptions');
            setCaptureQueue('duplicate');
          } else if (view === 'capture-unknown') {
            setCurrentView('capture');
            setCaptureTab('exceptions');
            setCaptureQueue('unknown');
          } else if (filter === 'upload') {
            setCurrentView('harvest');
            setHarvestTab('upload');
            setHarvestUploadFilter('pending');
          } else if (filter === 'failed') {
            setCurrentView('harvest');
            setHarvestTab('upload');
            setHarvestUploadFilter('failed');
          } else if (filter) {
            handleNavigateWithFilter(view, filter);
          } else {
            setCurrentView(view);
            // Reset harvest tab to picklist when navigating to harvest without a filter
            if (view === 'harvest') {
              setHarvestTab('picklist');
              setHarvestUploadFilter('pending');
            }
          }
        }} />;
      case 'harvest':
        return <HarvestView initialFilter={harvestFilter} initialTab={harvestTab} initialUploadFilter={harvestUploadFilter} />;
      case 'capture':
        return <CaptureView initialTab={captureTab} initialQueue={captureQueue} />;
      case 'library':
        return <LibraryView />;
      case 'admin':
        return <AdminView resetTrigger={adminResetTrigger} />;
      default:
        return <Dashboard onNavigate={setCurrentView} />;
    }
  };

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/signed-out" element={<SignedOut />} />
      <Route path="*" element={
        <Layout currentView={currentView} onNavigate={handleSidebarNavigate}>
          {renderCurrentView()}
          <div className="App" style={{ display: 'none' }}>
        <div className="App-content">
          <h1>Staff Portal - Component Testing</h1>
          <p>React + TypeScript Setup Complete</p>
        
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button onClick={handleButtonClick}>Primary Button</Button>
          <Button variant="secondary" onClick={handleButtonClick}>Secondary Button</Button>
          <Button variant="danger" size="small" onClick={handleButtonClick}>Danger Small</Button>
          <Button size="large" disabled>Disabled Large</Button>
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button onClick={() => openModal('small')}>Open Small Modal</Button>
          <Button variant="secondary" onClick={() => openModal('medium')}>Open Medium Modal</Button>
          <Button variant="danger" onClick={() => openModal('large')}>Open Large Modal</Button>
        </div>

        <div style={{ marginTop: '30px', maxWidth: '1000px', margin: '30px auto 0' }}>
          <Card title="Basic Card">
            <p>This is a basic card with a title and some content.</p>
            <Button size="small">Action Button</Button>
          </Card>

          <Card hoverable>
            <h4>Hoverable Card (No Title)</h4>
            <p>This card has hover effects but no title header.</p>
          </Card>

          <Card title="Clickable Card" onClick={handleCardClick} hoverable>
            <p>This card is clickable and hoverable. Click anywhere on the card!</p>
          </Card>

          <Card title="Sample Data Table">
            <Table 
              columns={tableColumns} 
              data={tableData} 
              onRowClick={handleRowClick}
            />
          </Card>

          <Card title="Empty Table Example">
            <Table 
              columns={tableColumns} 
              data={[]} 
              emptyMessage="No invoices found"
            />
          </Card>

          <Card title="Loading Table Example">
            <Table 
              columns={tableColumns} 
              data={[]} 
              loading={true}
            />
          </Card>

          <Card title="Input Components">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Search Invoices"
                    type="search"
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder="Enter customer name or invoice ID..."
                    size="medium"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Email Address"
                    type="email"
                    value={emailValue}
                    onChange={setEmailValue}
                    placeholder="user@example.com"
                    required
                    size="medium"
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Password"
                    type="password"
                    value={passwordValue}
                    onChange={setPasswordValue}
                    placeholder="Enter password"
                    size="medium"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Disabled Input"
                    value="This field is disabled"
                    onChange={() => {}}
                    disabled
                    size="medium"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Small Input"
                    value=""
                    onChange={() => {}}
                    placeholder="Small size"
                    size="small"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Input
                    label="Large Input"
                    value=""
                    onChange={() => {}}
                    placeholder="Large size"
                    size="large"
                  />
                </div>
              </div>

              <div style={{ maxWidth: '300px' }}>
                <Input
                  label="Input with Error"
                  value="invalid@email"
                  onChange={() => {}}
                  error="Please enter a valid email address"
                  size="medium"
                />
              </div>
            </div>
          </Card>

          <Card title="Select Components">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Utility Type"
                    value={utilityType}
                    onChange={setUtilityType}
                    options={[
                      { value: 'electric', label: 'Electric' },
                      { value: 'gas', label: 'Gas' },
                      { value: 'water', label: 'Water' },
                      { value: 'cws', label: 'Chilled Water Steam' }
                    ]}
                    placeholder="Select utility type..."
                    required
                    size="medium"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Customer"
                    value={customerSelect}
                    onChange={setCustomerSelect}
                    options={[
                      { value: 'church', label: 'Allegheny Center Alliance Church' },
                      { value: 'property', label: 'Sample Property LLC' },
                      { value: 'disabled', label: 'Disabled Option', disabled: true }
                    ]}
                    placeholder="Select customer..."
                    size="medium"
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Status Filter"
                    value={statusSelect}
                    onChange={setStatusSelect}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'received', label: 'Received' },
                      { value: 'overdue', label: 'Overdue' },
                      { value: 'processed', label: 'Processed' }
                    ]}
                    size="medium"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Disabled Select"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'option1', label: 'Option 1' },
                      { value: 'option2', label: 'Option 2' }
                    ]}
                    disabled
                    size="medium"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Small Select"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'small1', label: 'Small Option 1' },
                      { value: 'small2', label: 'Small Option 2' }
                    ]}
                    placeholder="Small size..."
                    size="small"
                  />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <Select
                    label="Large Select"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'large1', label: 'Large Option 1' },
                      { value: 'large2', label: 'Large Option 2' }
                    ]}
                    placeholder="Large size..."
                    size="large"
                  />
                </div>
              </div>

              <div style={{ maxWidth: '300px' }}>
                <Select
                  label="Select with Error"
                  value=""
                  onChange={() => {}}
                  options={[
                    { value: 'error1', label: 'Error Option 1' },
                    { value: 'error2', label: 'Error Option 2' }
                  ]}
                  error="Please select a valid option"
                  size="medium"
                />
              </div>
            </div>
          </Card>
        </div>

        <Modal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          title={`${modalSize.charAt(0).toUpperCase() + modalSize.slice(1)} Modal Example`}
          size={modalSize}
        >
          <p>This is a {modalSize} modal with sample content.</p>
          <p>You can close this modal by:</p>
          <ul>
            <li>Clicking the × button in the top right</li>
            <li>Pressing the Escape key</li>
            <li>Clicking outside the modal (on the overlay)</li>
            <li>Using the Close button below</li>
          </ul>
          
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button onClick={closeModal}>Close Modal</Button>
          </div>
        </Modal>
        </div>
      </div>
    </Layout>
      } />
    </Routes>
  );
}

export default App;
